import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    titleTextLines = "titleTextLines",
    bgImageUrl = "bgImageUrl",
    buttonText = "buttonText",
    buttonLink = "buttonLink",
    buttonColor = "buttonColor",
    customCSS = "customCSS",
}

interface TitleTextLineModel {
    fields: {
        line: string;
    };
}

interface OwnProps {
    extendedStyles: WidgetBravoStyles;
    fields: {
        [fields.titleTextLines]: TitleTextLineModel[];
        [fields.bgImageUrl]: string;
        [fields.buttonText]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonColor]: string;
        [fields.customCSS]: string;
    };
}

type Props = OwnProps;

export interface WidgetBravoStyles {
    wrapper?: InjectableCss;
    mainPanel?: InjectableCss;
    imagePanel?: InjectableCss;
    image?: LazyImageProps;
    topSubPanel?: InjectableCss;
    topSubPanelTextWrapper?: InjectableCss;
    topSubPanelText?: TypographyPresentationProps;
    bottomSubPanel?: InjectableCss;
    bottomSubPanelButtonWrapper: InjectableCss;
    button?: ButtonPresentationProps;
}

export const widgetBravoStyles: WidgetBravoStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-bottom: 30px;
        `,
    },
    mainPanel: {
        css: css`
            width: 100%;
            height: auto;
            position: relative;
        `,
    },
    imagePanel: {
        css: css`
            width: 100%;
            height: auto;
        `,
    },
    topSubPanel: {
        css: css`
            height: 50%;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-end;
            padding-bottom: 20px;
            position: absolute;
            top: 0;
            left: 0;
        `,
    },
    topSubPanelTextWrapper: {
        css: css``,
    },
    topSubPanelText: {
        css: css`
            color: white;
            text-transform: uppercase;
            font-weight: 600;
            font-size: 18px;
        `,
    },
    bottomSubPanel: {
        css: css`
            height: 50%;
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            padding-top: 20px;
            position: absolute;
            top: 50%;
            left: 0;
        `,
    },
    bottomSubPanelButtonWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
        `,
    },
    button: {
        typographyProps: {
            color: "button.main",
            size: 12,
            css: css`
                text-transform: uppercase;
            `,
        },
    },
};

const WidgetBravo: FC<Props> = props => {
    const { extendedStyles, fields } = props;
    const { titleTextLines, bgImageUrl, buttonText, buttonLink, buttonColor, customCSS } = fields;

    const [styles] = React.useState(() => mergeToNew(widgetBravoStyles, extendedStyles));

    const history = useHistory();
    const { url } = useGetLink(buttonLink);
    const { isInShell } = useShellContext();

    const handleButtonClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const customCssWrapper = {
        css: css`
            ${customCSS}
        `,
    };

    const buttonStyle = {
        ...styles.button,
        color: buttonColor,
    };

    return (
        <StyledWrapper {...customCssWrapper} data-test-selector="widgetBravo_customCssWrapper">
            <StyledWrapper {...styles.wrapper} className="wrapper" data-test-selector="wrapper">
                <StyledWrapper {...styles.mainPanel} data-test-selector="mainPanel">
                    <StyledWrapper {...styles.imagePanel} data-test-selector="imagePanel">
                        <LazyImage src={bgImageUrl} {...styles.image} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.topSubPanel} data-test-selector="topSubPanel">
                        {titleTextLines &&
                            titleTextLines.map(item => (
                                <StyledWrapper {...styles.topSubPanelTextWrapper} key={item.fields.line}>
                                    <Typography {...styles.topSubPanelText}>{item.fields.line}</Typography>
                                </StyledWrapper>
                            ))}
                    </StyledWrapper>
                    <StyledWrapper {...styles.bottomSubPanel} data-test-selector="bottomSubPanel">
                        <StyledWrapper {...styles.bottomSubPanelButtonWrapper}>
                            <Button {...buttonStyle} onClick={handleButtonClick}>
                                {buttonText}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {

}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 1,
};

const buttonTab = {
    displayName: "Button",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const widgetModule: WidgetModule = {
    component: WidgetBravo,
    definition: {
        group: "Basic",
        displayName: "Widget Bravo",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.titleTextLines,
                displayName: "Title Text Lines",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.line;
                },
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "line",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
                tab: basicTab,
                sortOrder: 20,
            },
            {
                name: fields.bgImageUrl,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
                tab: basicTab,
                sortOrder: 30,
            },
            {
                name: fields.buttonText,
                displayName: "Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                tooltip: "",
                tab: buttonTab,
                sortOrder: 110,
            },
            {
                name: fields.buttonLink,
                fieldType: "General",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: buttonTab,
                sortOrder: 120,
            },
            {
                name: fields.buttonColor,
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: "transparent",
                tab: buttonTab,
                sortOrder: 130,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                sortOrder: 200,
                defaultValue: defaultCustomCss,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
